import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trash2,
  RotateCcw,
  X,
  ZoomIn,
  Clock,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Phone,
  Calendar,
  FolderOpen,
  FileText,
  CheckSquare,
  Square,
  Check,
  Image as ImageIcon,
  HardDrive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrashPage() {
  const [items, setItems] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [itemToAction, setItemToAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("images");
  
  // Bulk selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedPdfs, setSelectedPdfs] = useState([]);
  const [designItems, setDesignItems] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [designStats, setDesignStats] = useState({ total_items: 0, total_size_mb: 0, total_size_gb: 0 });
  const [bulkRestoreDialogOpen, setBulkRestoreDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchTrashItems();
    fetchStats();
    fetchDesignItems();
    
    // Auto-refresh every minute to update time remaining
    const interval = setInterval(() => {
      fetchTrashItems();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchTrashItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/trash/items`);
      setItems(response.data.items || []);
      setPdfs(response.data.pdfs || []);
    } catch (error) {
      console.error("Error fetching trash items:", error);
      toast.error("Failed to load trash items");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/trash/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchDesignItems = async () => {
    try {
      const response = await axios.get(`${API}/trash/designs`);
      setDesignItems(response.data.items || []);
      setDesignStats({
        total_items: response.data.total_items || 0,
        total_size_mb: response.data.total_size_mb || 0,
        total_size_gb: response.data.total_size_gb || 0
      });
    } catch (error) {
      console.error("Error fetching design items:", error);
    }
  };

  const handleRestore = async () => {
    if (!itemToAction) return;
    
    setActionLoading(true);
    try {
      if (itemToAction.type === "pdf") {
        await axios.post(`${API}/trash/restore-pdf/${encodeURIComponent(itemToAction.filename)}`);
        toast.success("PDF restored successfully");
      } else {
        await axios.post(`${API}/trash/restore/${encodeURIComponent(itemToAction.relative_path)}`);
        toast.success("Image restored successfully");
      }
      setRestoreDialogOpen(false);
      setItemToAction(null);
      fetchTrashItems();
      fetchStats();
    } catch (error) {
      console.error("Error restoring item:", error);
      toast.error("Failed to restore item");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!itemToAction) return;
    
    setActionLoading(true);
    try {
      if (itemToAction.type === "pdf") {
        await axios.delete(`${API}/trash/permanent-pdf/${encodeURIComponent(itemToAction.filename)}`);
        toast.success("PDF permanently deleted");
      } else {
        await axios.delete(`${API}/trash/permanent/${encodeURIComponent(itemToAction.relative_path)}`);
        toast.success("Image permanently deleted");
      }
      setDeleteDialogOpen(false);
      setItemToAction(null);
      fetchTrashItems();
      fetchStats();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEmptyTrash = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/trash/empty`);
      toast.success("Trash emptied successfully");
      setEmptyTrashDialogOpen(false);
      fetchTrashItems();
      fetchStats();
    } catch (error) {
      console.error("Error emptying trash:", error);
      toast.error("Failed to empty trash");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCleanupExpired = async () => {
    try {
      const response = await axios.post(`${API}/trash/cleanup`);
      toast.success(`Cleaned up ${response.data.deleted_count} expired items`);
      fetchTrashItems();
      fetchStats();
    } catch (error) {
      console.error("Error cleaning up:", error);
      toast.error("Failed to cleanup expired items");
    }
  };

  // Bulk selection functions
  const toggleSelectionMode = () => {
    if (selectionMode) {
      setSelectedImages([]);
      setSelectedPdfs([]);
    }
    setSelectionMode(!selectionMode);
  };

  const toggleImageSelection = (path) => {
    setSelectedImages(prev => 
      prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
    );
  };

  const togglePdfSelection = (filename) => {
    setSelectedPdfs(prev => 
      prev.includes(filename) ? prev.filter(f => f !== filename) : [...prev, filename]
    );
  };

  const selectAllImages = () => {
    if (selectedImages.length === items.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(items.map(item => item.relative_path));
    }
  };

  const selectAllPdfs = () => {
    if (selectedPdfs.length === pdfs.length) {
      setSelectedPdfs([]);
    } else {
      setSelectedPdfs(pdfs.map(pdf => pdf.filename));
    }
  };

  const selectAllDesigns = () => {
    if (selectedDesigns.length === designItems.length) {
      setSelectedDesigns([]);
    } else {
      setSelectedDesigns(designItems.map(item => item.order_id));
    }
  };

  const toggleDesignSelection = (orderId) => {
    setSelectedDesigns(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleDeleteDesignCache = async (orderId) => {
    try {
      setActionLoading(true);
      await axios.delete(`${API}/trash/designs/${orderId}`);
      toast.success("Design cache deleted");
      fetchDesignItems();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete design cache");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDeleteDesigns = async () => {
    try {
      setBulkActionLoading(true);
      const response = await axios.post(`${API}/trash/designs/bulk-delete`, {
        order_ids: selectedDesigns
      });
      toast.success(`Deleted ${response.data.deleted} design caches, freed ${response.data.freed_mb} MB`);
      setSelectedDesigns([]);
      fetchDesignItems();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete design caches");
    } finally {
      setBulkActionLoading(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleClearAllDesigns = async () => {
    try {
      setActionLoading(true);
      const response = await axios.delete(`${API}/trash/designs/clear-all`);
      toast.success(`Cleared all design cache, freed ${response.data.freed_gb} GB`);
      fetchDesignItems();
      fetchStats();
    } catch (error) {
      toast.error("Failed to clear design cache");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkRestore = async () => {
    setBulkActionLoading(true);
    try {
      const response = await axios.post(`${API}/trash/bulk-restore`, {
        image_paths: selectedImages,
        pdf_filenames: selectedPdfs
      });
      
      toast.success(`Restored ${response.data.total_restored} item(s)`);
      setBulkRestoreDialogOpen(false);
      setSelectedImages([]);
      setSelectedPdfs([]);
      setSelectionMode(false);
      fetchTrashItems();
      fetchStats();
    } catch (error) {
      console.error("Error bulk restoring:", error);
      toast.error("Failed to restore items");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkActionLoading(true);
    try {
      const response = await axios.post(`${API}/trash/bulk-delete`, {
        image_paths: selectedImages,
        pdf_filenames: selectedPdfs
      });
      
      toast.success(`Permanently deleted ${response.data.total_deleted} item(s)`);
      setBulkDeleteDialogOpen(false);
      setSelectedImages([]);
      setSelectedPdfs([]);
      setSelectionMode(false);
      fetchTrashItems();
      fetchStats();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Failed to delete items");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return "Expiring soon";
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getTimeRemainingColor = (seconds) => {
    if (!seconds || seconds <= 0) return "text-red-600";
    if (seconds < 3600) return "text-orange-600";
    if (seconds < 6 * 3600) return "text-yellow-600";
    return "text-emerald-600";
  };

  // Group items by phone number
  const groupedItems = items.reduce((acc, item) => {
    const key = `${item.phone_number}.${item.session_id}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const totalSelected = selectedImages.length + selectedPdfs.length + selectedDesigns.length;
  const totalItems = items.length + pdfs.length;

  return (
    <div className="flex h-screen overflow-hidden" data-testid="trash-page">
      {/* Sidebar */}
      <Sidebar stats={stats} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Trash2 className="h-6 w-6 text-red-500" />
              Trash
            </h1>
            <p className="text-sm text-muted-foreground">
              Deleted items are kept for 24 hours before permanent deletion
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectionMode ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (activeTab === "images") selectAllImages();
                    else selectAllPdfs();
                  }}
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  {activeTab === "images" 
                    ? (selectedImages.length === items.length ? "Deselect All" : `Select All (${items.length})`)
                    : (selectedPdfs.length === pdfs.length ? "Deselect All" : `Select All (${pdfs.length})`)
                  }
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setBulkRestoreDialogOpen(true)}
                  disabled={totalSelected === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Restore ({totalSelected})
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  disabled={totalSelected === 0}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({totalSelected})
                </Button>
                <Button variant="ghost" size="sm" onClick={toggleSelectionMode}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {totalItems > 0 && (
                  <Button variant="outline" size="sm" onClick={toggleSelectionMode}>
                    <CheckSquare className="h-4 w-4 mr-2" />
                    Select
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleCleanupExpired}>
                  <Clock className="h-4 w-4 mr-2" />
                  Cleanup Expired
                </Button>
                <Button variant="outline" size="sm" onClick={() => { fetchTrashItems(); fetchStats(); }}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                {totalItems > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => setEmptyTrashDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Empty Trash
                  </Button>
                )}
              </>
            )}
          </div>
        </header>

        {/* Stats */}
        <div className="px-6 py-4 grid grid-cols-4 gap-3 max-w-2xl">
          <Card className="shadow-soft bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-700">{stats.total_items || 0}</div>
              <div className="text-xs text-muted-foreground">Images</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-700">{stats.total_pdfs || 0}</div>
              <div className="text-xs text-muted-foreground">PDFs</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-700">{stats.total_all || 0}</div>
              <div className="text-xs text-muted-foreground">Total Items</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{formatFileSize(stats.total_size)}</div>
              <div className="text-xs text-muted-foreground">Total Size</div>
            </CardContent>
          </Card>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 pb-6 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="mb-4">
              <TabsTrigger value="images" className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Images ({items.length})
              </TabsTrigger>
              <TabsTrigger value="pdfs" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                PDFs ({pdfs.length})
              </TabsTrigger>
              <TabsTrigger value="designs" className="flex items-center gap-2">
                <HardDrive className="h-4 w-4" />
                Designs ({designItems.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="images" className="flex-1 overflow-hidden mt-0">
              <Card className="h-full shadow-soft">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Deleted Images
                  </CardTitle>
                  <CardDescription>
                    Images will be automatically deleted after 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <ImageIcon className="h-16 w-16 text-muted-foreground/30 mb-4" />
                      <p className="text-muted-foreground font-medium">No images in trash</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[calc(100vh-420px)]">
                      <div className="p-4 space-y-4">
                        {Object.entries(groupedItems).map(([key, groupItems]) => (
                          <div key={key} className="border rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <span className="font-mono text-sm">{groupItems[0].phone_number}</span>
                              <Badge variant="outline">{groupItems[0].session_id}</Badge>
                              <Badge variant="secondary">{groupItems.length} images</Badge>
                              <span className="text-xs text-muted-foreground ml-auto">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                {groupItems[0].date}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                              {groupItems.map((item, index) => {
                                const isSelected = selectedImages.includes(item.relative_path);
                                return (
                                  <div
                                    key={item.path}
                                    className={`group relative aspect-square rounded-lg overflow-hidden bg-muted border-2 transition-all ${
                                      isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent'
                                    }`}
                                    data-testid={`trash-item-${index}`}
                                    onClick={() => selectionMode && toggleImageSelection(item.relative_path)}
                                  >
                                    <img
                                      src={`${BACKEND_URL}${item.path}`}
                                      alt={item.filename}
                                      className={`w-full h-full object-cover ${isSelected ? 'opacity-70' : 'opacity-70'}`}
                                      loading="lazy"
                                    />
                                    
                                    {/* Selection checkbox */}
                                    {selectionMode && (
                                      <div className="absolute top-1 left-1 z-20">
                                        <div className={`h-6 w-6 rounded flex items-center justify-center ${
                                          isSelected ? 'bg-blue-500 text-white' : 'bg-white/90 border-2 border-slate-300'
                                        }`}>
                                          {isSelected && <Check className="h-4 w-4" />}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Time remaining badge */}
                                    <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded text-xs font-medium bg-black/70 ${getTimeRemainingColor(item.time_remaining)}`}>
                                      {formatTimeRemaining(item.time_remaining)}
                                    </div>
                                    
                                    {/* Hover Actions - hide in selection mode */}
                                    {!selectionMode && (
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                        <Button
                                          variant="secondary"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => setSelectedImage(item)}
                                          title="View"
                                        >
                                          <ZoomIn className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="secondary"
                                          size="icon"
                                          className="h-8 w-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                                          onClick={() => {
                                            setItemToAction(item);
                                            setRestoreDialogOpen(true);
                                          }}
                                          title="Restore"
                                        >
                                          <RotateCcw className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="destructive"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => {
                                            setItemToAction(item);
                                            setDeleteDialogOpen(true);
                                          }}
                                          title="Delete Permanently"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                    
                                    {/* Filename */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1">
                                      <p className="text-xs text-white font-mono truncate">{item.filename}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pdfs" className="flex-1 overflow-hidden mt-0">
              <Card className="h-full shadow-soft">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-500" />
                    Deleted PDFs
                  </CardTitle>
                  <CardDescription>
                    PDFs will be automatically deleted after 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : pdfs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <FileText className="h-16 w-16 text-muted-foreground/30 mb-4" />
                      <p className="text-muted-foreground font-medium">No PDFs in trash</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[calc(100vh-420px)]">
                      <div className="p-4 space-y-2">
                        {pdfs.map((pdf, index) => {
                          const isSelected = selectedPdfs.includes(pdf.filename);
                          return (
                            <div
                              key={pdf.filename}
                              className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                                isSelected ? 'border-blue-500 bg-blue-50' : 'border-transparent bg-muted/50 hover:bg-muted'
                              }`}
                              data-testid={`trash-pdf-${index}`}
                              onClick={() => selectionMode && togglePdfSelection(pdf.filename)}
                            >
                              {/* Selection checkbox */}
                              {selectionMode && (
                                <div className={`h-6 w-6 rounded flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'bg-blue-500 text-white' : 'bg-white border-2 border-slate-300'
                                }`}>
                                  {isSelected && <Check className="h-4 w-4" />}
                                </div>
                              )}
                              
                              {/* PDF Icon */}
                              <div className="h-12 w-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                                <FileText className="h-6 w-6 text-red-600" />
                              </div>
                              
                              {/* PDF Info */}
                              <div className="flex-1 min-w-0">
                                <p className="font-mono text-sm truncate">{pdf.filename}</p>
                                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                  <span>{formatFileSize(pdf.size)}</span>
                                  {pdf.page_count > 0 && <span>{pdf.page_count} pages</span>}
                                  {pdf.order_count > 0 && <span>{pdf.order_count} orders</span>}
                                </div>
                              </div>
                              
                              {/* Time remaining */}
                              <div className={`text-sm font-medium ${getTimeRemainingColor(pdf.time_remaining)}`}>
                                {formatTimeRemaining(pdf.time_remaining)}
                              </div>
                              
                              {/* Actions - hide in selection mode */}
                              {!selectionMode && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`${BACKEND_URL}${pdf.path}`, '_blank');
                                    }}
                                    title="Preview"
                                  >
                                    <ZoomIn className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setItemToAction({ ...pdf, type: "pdf" });
                                      setRestoreDialogOpen(true);
                                    }}
                                    title="Restore"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-red-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setItemToAction({ ...pdf, type: "pdf" });
                                      setDeleteDialogOpen(true);
                                    }}
                                    title="Delete Permanently"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="designs" className="flex-1 overflow-hidden mt-0">
              <Card className="h-full shadow-soft">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <HardDrive className="h-4 w-4 text-orange-500" />
                        Design Cache
                      </CardTitle>
                      <CardDescription>
                        Preview cache files. Clear to free disk space. Will be regenerated on next preview.
                      </CardDescription>
                    </div>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleClearAllDesigns}
                      disabled={designItems.length === 0 || actionLoading}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clear All ({designStats.total_size_gb?.toFixed(2)} GB)
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-0">
                  {designItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                      <HardDrive className="h-12 w-12 mb-4 opacity-50" />
                      <p>No design cache</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[calc(100vh-400px)]">
                      <div className="p-4 space-y-2">
                        {designItems.map((item) => (
                          <div
                            key={item.order_id}
                            className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {selectionMode && (
                                <Checkbox
                                  checked={selectedDesigns.includes(item.order_id)}
                                  onCheckedChange={() => toggleDesignSelection(item.order_id)}
                                />
                              )}
                              <div className="p-2 rounded-lg bg-orange-100">
                                <HardDrive className="h-5 w-5 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{item.order_display_id}</p>
                                <p className="text-xs text-muted-foreground">
                                  {item.phone_number} • {item.file_count} files • {item.size_mb} MB
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={item.design_status === "READY" ? "default" : "secondary"}>
                                {item.design_status}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDesignCache(item.order_id)}
                                disabled={actionLoading}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Restore Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore {itemToAction?.type === "pdf" ? "PDF" : "Image"}</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore &quot;{itemToAction?.filename}&quot; to its original location.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRestore} 
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{itemToAction?.filename}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handlePermanentDelete} 
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Empty Trash Dialog */}
      <AlertDialog open={emptyTrashDialogOpen} onOpenChange={setEmptyTrashDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Empty Trash</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {totalItems} items in the trash. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleEmptyTrash} 
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Empty Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Restore Dialog */}
      <AlertDialog open={bulkRestoreDialogOpen} onOpenChange={setBulkRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-emerald-600" />
              Restore {totalSelected} Item{totalSelected > 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedImages.length > 0 && <span>{selectedImages.length} image(s)</span>}
              {selectedImages.length > 0 && selectedPdfs.length > 0 && <span> and </span>}
              {selectedPdfs.length > 0 && <span>{selectedPdfs.length} PDF(s)</span>}
              {' '}will be restored to their original locations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkRestore} 
              disabled={bulkActionLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {bulkActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Restore All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Permanently Delete {totalSelected} Item{totalSelected > 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedImages.length > 0 && <span>{selectedImages.length} image(s)</span>}
              {selectedImages.length > 0 && selectedPdfs.length > 0 && <span> and </span>}
              {selectedPdfs.length > 0 && <span>{selectedPdfs.length} PDF(s)</span>}
              {' '}will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkActionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              disabled={bulkActionLoading}
              className="bg-destructive text-destructive-foreground"
            >
              {bulkActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
          data-testid="trash-image-lightbox"
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setSelectedImage(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={`${BACKEND_URL}${selectedImage.path}`}
            alt={selectedImage.filename}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/60 rounded-lg px-4 py-2">
            <span className="text-white text-sm font-mono">{selectedImage.filename}</span>
            <span className={`text-sm ${getTimeRemainingColor(selectedImage.time_remaining)}`}>
              {formatTimeRemaining(selectedImage.time_remaining)}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-400 hover:text-emerald-300 hover:bg-white/10"
              onClick={(e) => {
                e.stopPropagation();
                setItemToAction(selectedImage);
                setRestoreDialogOpen(true);
              }}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Restore
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
